import { get } from 'lodash';
import type { BadgeOptions, ImageOptions } from '../../visualization-schema';

// Helper function to safely parse JSON
export const safeJSONParse = (content: unknown): unknown => {
  if (typeof content !== 'string') {
    return content;
  }

  try {
    return JSON.parse(content);
  } catch (e) {
    return content;
  }
};

// Helper function to format column header
export const formatColumnHeader = (key: string): string => {
  const words = key
    .split(/(?=[A-Z])|[_-]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
  return words.join(' ');
};

// Helper function to convert schema image options to component props
export const convertImageOptions = (options?: ImageOptions) => {
  if (!options) {
    return undefined;
  }

  return {
    type: options.type || 'resourceType',
    fallback: options.fallback,
    style: options.style,
    className: options.className,
  };
};

// Helper function to map status to badge status
export const mapStatusToBadgeStatus = (
  status: string,
): BadgeOptions['status'] => {
  switch (status.toLowerCase()) {
    case 'active':
    case 'success':
    case 'completed':
      return 'good';
    case 'pending':
    case 'in_progress':
      return 'warning';
    case 'error':
    case 'failed':
      return 'bad';
    default:
      return 'neutral';
  }
};

// Helper function to format full datetime
export const formatFullDateTime = (dateStr: string) => {
  try {
    return new Date(dateStr).toLocaleString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    });
  } catch (e) {
    return dateStr;
  }
};

/**
 * Process a template string with JSON path expressions
 * Example: "https://example.com/{{id}}" or "{{url}}"
 * Also supports nested paths: "{{meta.url}}"
 * And array access: "{{links[0]}}"
 */
export function processTemplate(
  template: string,
  data: Record<string, unknown>,
): string {
  if (!template) {
    return '';
  }

  return template.replace(/\{\{([^}]+)\}\}/g, (match, path) => {
    // Trim whitespace from the path
    const trimmedPath = path.trim();

    // Handle nested paths and array access
    const value = get(data, trimmedPath);

    // If value is undefined or null, return empty string
    if (value === undefined || value === null) {
      return '';
    }

    // Convert to string and handle arrays/objects
    if (Array.isArray(value)) {
      return value.join(', ');
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value);
  });
}

/**
 * Check if a string looks like a URL
 */
export function isUrl(value: unknown): boolean {
  if (typeof value !== 'string') {
    return false;
  }
  return value.startsWith('http://') || value.startsWith('https://');
}
